import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Question, QuestionFilterCount } from '../../models/QAndA';
import QAndASellerContainer from '../QAndASellerContainer';
import QAndABuyerContainer from '../QAndABuyerContainer';
import QAndAContainerProvider from '../../providers/QAndA';
import QAndARoleFilter from '../QAndARoleFilter';
import ResolvePagination from 'components/ResolvePagination';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Button, Loader } from 'ncoded-component-library';
import MembersImage from 'assets/images/members.webp';
import ResolveImage from 'components/ResolveImage';
import { useTranslation } from 'react-i18next';
import ChatIcon from 'icons/Chat.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from '../SideDrawer/SideDrawer.component';
import dataRoomApi from '../../dataRoomApi';
import FloatingButton from 'components/FloatingButton';
import ManageQuestionSubjectsDrawer from '../ManageQuestionSubjectsDrawer';
import SettingsIcon from 'icons/Settings.icon';
import { AskQuestionDrawer } from '../QAndASellerContainer/components/AskQuestionDrawer';
import {
  FITLER_PARAMS_TO_EMPTY_STATE,
  IN_REVIEW_QUERY_BUY_SIDE,
  UNANSWERED_SELL_SIDE_QUERY,
} from '../QAndARoleFilter/data';
import useEffectOnceWhen from 'hooks/useEffectOnce';
import useQueryFilter from 'hooks/useQueryFilter';
import usePageParams from 'hooks/usePageParams';
import AskQuestionIcon from 'icons/AskQuestion.icon';
import PlusIcon from 'icons/Plus.icon';

import './QAndAPaginatedContainer.styles.scss';
import './QAndAPaginatedContainer.styles.responsive.scss';

type QAndAPaginatedContainerProps = {
  className?: string;
};

const Q_AND_A_PAGE_LIMIT = '20';

const QAndAPaginatedContainer: React.FC<QAndAPaginatedContainerProps> = (
  props,
) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [allowFetch, setAllowFetch] = useState(false);
  const [filterCount, setFilterCount] = useState<QuestionFilterCount>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [displayedQuestions, setDisplayedQuestions] = useState<Question[]>([]);
  const [isQAEmpty, setIsQAEmpty] = useState(false);

  const [manageQuestionSubjectDrawer, manageQuestionSubjectDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);
  const [askSellerQ, askSellerQRef] = useCallbackRef<SideDrawerRef>(null);

  const {
    canUserAskQuestion,
    canUserManageQuestionSubject,
    refetchFlag,
    doesntHaveAccess,
    isQaAdmin,
    isBuySide,
    dataRoom: { _id: dataRoomId },
    openBuyQaDrawer,
    refetch,
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const page = usePageParams();
  const {
    params: { $or, sort },
    onFiltersApply,
  } = useQueryFilter();

  const { t } = useTranslation();

  const classes = classNames(
    'q-and-a-paginated-container',
    'white-panel',
    className,
  );

  const afterDeleteSuccess = useCallback((name: string) => {
    setDisplayedQuestions((prev) =>
      prev.map((q) =>
        q.subject === name ? { ...q, subject: 'Other', otherSubject: name } : q,
      ),
    );
  }, []);

  const afterEditSuccess = useCallback((newName: string, oldName: string) => {
    setDisplayedQuestions((prev) =>
      prev.map((q) => (q.subject === oldName ? { ...q, subject: newName } : q)),
    );
  }, []);

  const openSellQaDrawer = useCallback(
    () => askSellerQ.setOpen(true),
    [askSellerQ],
  );

  const openQaDrawer = useCallback(() => {
    if (doesntHaveAccess) return;

    if (isQaAdmin) openSellQaDrawer();
    else if (isBuySide) openBuyQaDrawer();
  }, [
    doesntHaveAccess,
    isQaAdmin,
    isBuySide,
    openBuyQaDrawer,
    openSellQaDrawer,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await dataRoomApi.questionsAndAnswers.getFitlerCounts(
          dataRoomId,
        );

        setFilterCount(data);
        setIsQAEmpty(!data.allCount);
      } catch (e) {
        popServerError(e);
      }
    })();
  }, [dataRoomId, refetchFlag, popServerError]);

  useEffectOnceWhen(() => {
    setAllowFetch(true);

    if ($or) return;

    const hasInReview = !!filterCount?.inReviewCount;
    const hasUnanswered = !!filterCount?.unanswered;

    if (isBuySide && hasInReview) {
      onFiltersApply('$or', IN_REVIEW_QUERY_BUY_SIDE);
    }

    if (!isBuySide && hasUnanswered) {
      onFiltersApply('$or', UNANSWERED_SELL_SIDE_QUERY);
    }
  }, filterCount);

  useEffect(() => {
    if (!allowFetch) return;

    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { items, totalPages },
        } = await dataRoomApi.questionsAndAnswers.getQuestions(dataRoomId, {
          $page: page,
          $limit: Q_AND_A_PAGE_LIMIT,
          ...(sort ? { $sort: sort } : {}),
          ...($or ? { $or } : {}),
        });
        setTotalPages(totalPages);
        setDisplayedQuestions(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [allowFetch, $or, dataRoomId, page, refetchFlag, sort, popServerError]);

  const emptyQAState = useMemo(
    () => (
      <div className="q-and-a-paginated-container__empty">
        <ResolveImage src={MembersImage} asIllustration />
        <p>{t(FITLER_PARAMS_TO_EMPTY_STATE[$or] || 'noQuestionsYet')}</p>

        <div className="actions">
          {canUserAskQuestion && (
            <Button variant="outline" onClick={openQaDrawer} icon={ChatIcon}>
              {t(
                isQAEmpty && !isQaAdmin
                  ? 'askYourFirstQuestion'
                  : 'askAQuestion',
              )}
            </Button>
          )}

          {canUserManageQuestionSubject && (
            <Button
              variant="outline"
              onClick={() => manageQuestionSubjectDrawer?.setOpen(true)}
              icon={<SettingsIcon strokeWidth={2} />}
            >
              {t('manageQ&Asubjects')}
            </Button>
          )}
        </div>
      </div>
    ),
    [
      canUserAskQuestion,
      canUserManageQuestionSubject,
      isQaAdmin,
      isQAEmpty,
      manageQuestionSubjectDrawer,
      $or,
      t,
      openQaDrawer,
    ],
  );

  return (
    <div className={classes}>
      <QAndAContainerProvider
        displayedQuestions={displayedQuestions}
        filterCount={filterCount}
        refetch={refetch}
        setDisplayedQuestions={setDisplayedQuestions}
      >
        {isQAEmpty ? (
          emptyQAState
        ) : (
          <>
            <div className="q-and-a-paginated-container__filters-wrapper">
              <QAndARoleFilter />

              <div className="actions">
                {canUserAskQuestion && (
                  <Button icon={PlusIcon} onClick={openQaDrawer}>
                    {t('newQuestion')}
                  </Button>
                )}

                {canUserManageQuestionSubject && (
                  <Button
                    variant="icon"
                    icon={SettingsIcon}
                    onClick={() => manageQuestionSubjectDrawer?.setOpen(true)}
                  />
                )}
              </div>
            </div>

            {isLoading ? (
              <Loader overlay={false} />
            ) : (
              <>
                {!displayedQuestions?.length ? (
                  emptyQAState
                ) : isBuySide ? (
                  <QAndABuyerContainer />
                ) : (
                  <QAndASellerContainer />
                )}

                <ResolvePagination totalPages={totalPages} />
              </>
            )}
          </>
        )}

        {isQaAdmin && (
          <>
            <FloatingButton icon={AskQuestionIcon} onClick={openSellQaDrawer} />
            <AskQuestionDrawer
              onSubmitSuccess={refetch}
              ref={askSellerQRef}
              onClose={() => askSellerQ?.setOpen(false)}
            />
          </>
        )}

        {canUserManageQuestionSubject && (
          <ManageQuestionSubjectsDrawer
            ref={manageQuestionSubjectDrawerRef}
            afterDeleteSuccess={afterDeleteSuccess}
            afterEditSuccess={afterEditSuccess}
          />
        )}
      </QAndAContainerProvider>
    </div>
  );
};

export default QAndAPaginatedContainer;
